/**
 * /auth/redeem (CHAP-284) - the magic-link landing. Reads ?token, exchanges it
 * for a session, then routes into the app (or shows expired/invalid).
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../lib/api.js';
import { takeStoredReturnPath } from '../../shared/return-path.js';

export default function Redeem() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setError('missing');
      return;
    }
    (async () => {
      try {
        await apiPost('/api/auth/magic-link/redeem', { token });
        await qc.invalidateQueries({ queryKey: ['me'] });
        // CHAP-594 review: a stored path is followed only when it is a
        // same-origin path (shared/return-path.ts), whenever it was stored.
        const back = takeStoredReturnPath(localStorage);
        navigate(back ?? '/c/omega-zeta', { replace: true });
      } catch (e) {
        const msg = e instanceof Error ? e.message : '';
        setError(msg.includes('410') ? 'expired' : 'invalid');
      }
    })();
  }, [token, navigate, qc]);

  async function handleResend() {
    setResending(true);
    try {
      await apiPost('/api/auth/resend', { token });
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 text-center">
        {!error ? (
          <p className="text-neutral text-sm animate-pulse">Signing you in…</p>
        ) : resent ? (
          <div className="space-y-3">
            <h1 className="text-navy text-lg font-bold">Check your email</h1>
            <p className="text-neutral text-sm">
              If that link was valid, a fresh sign-in link is on its way. It's valid for 30 minutes.
            </p>
            <Link to="/login" className="inline-block px-4 py-2 rounded bg-navy text-white text-sm font-medium">
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <h1 className="text-navy text-lg font-bold">
              {error === 'expired' ? 'This link has expired' : 'This sign-in link is invalid'}
            </h1>
            <p className="text-neutral text-sm">
              Sign-in links are single-use and valid for 30 minutes. Request a fresh one to continue.
            </p>
            {error === 'expired' ? (
              <>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="inline-block px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
                >
                  {resending ? 'Sending…' : 'Resend link'}
                </button>
                <div>
                  <Link to="/login" className="inline-block text-sm text-blue-600 underline">
                    Back to sign in
                  </Link>
                </div>
              </>
            ) : (
              <Link to="/login" className="inline-block px-4 py-2 rounded bg-navy text-white text-sm font-medium">
                Back to sign in
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
